import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, MessageCircle, X } from "lucide-react";
import { useLanguage } from "@/i18n";
import { ASSETS, WHATSAPP_URL } from "@/data";

const LANGS = [
  { code: "pt", label: "PT" },
  { code: "es", label: "ES" },
  { code: "en", label: "EN" },
];

export default function Header() {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);

  const links = [
    { id: "home", label: t.nav.home, href: "#home" },
    { id: "bots", label: t.nav.bots, href: "#bots" },
    { id: "products", label: t.nav.products, href: "#products" },
    { id: "contact", label: t.nav.contact, href: "#contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-cyan-400/10 bg-[#050714]/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a href="#home" data-testid="header-logo-link" className="group flex items-center gap-3">
          <span className="pixel-corners-sm block h-10 w-10 overflow-hidden border border-cyan-400/40 bg-[#0E1338] shadow-[0_0_18px_rgba(0,240,255,0.25)] transition-shadow duration-300 group-hover:shadow-[0_0_28px_rgba(0,240,255,0.5)]">
            <img src={ASSETS.logo} alt="NavX logo" className="h-full w-full object-cover" />
          </span>
          <span className="leading-none">
            <span className="font-pixel block text-sm tracking-widest text-white">NAVX</span>
            <span className="block text-[10px] uppercase tracking-[0.3em] text-cyan-300/80">
              Tecnologia
            </span>
          </span>
        </a>

        <nav data-testid="desktop-nav" className="hidden items-center gap-8 lg:flex">
          {links.map((link) => (
            <a
              key={link.id}
              href={link.href}
              data-testid={`nav-link-${link.id}`}
              className="group relative text-xs font-medium uppercase tracking-[0.22em] text-slate-300 transition-colors duration-300 hover:text-cyan-300"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-cyan-400 transition-[width] duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <div data-testid="language-switcher" className="flex items-center gap-1 border border-cyan-400/20 bg-[#0A0D24]/60 p-1">
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-btn-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`font-pixel px-2 py-1 text-[10px] tracking-widest transition-colors duration-200 ${
                  lang === l.code
                    ? "bg-cyan-400 text-[#050714]"
                    : "text-slate-400 hover:text-cyan-300"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-btn"
            className="pixel-corners-sm flex items-center gap-2 bg-cyan-400 px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#050714] transition-all duration-300 hover:bg-white hover:shadow-[0_0_24px_rgba(0,240,255,0.6)]"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </a>
        </div>

        <button
          data-testid="hamburger-btn"
          onClick={() => setOpen(!open)}
          className="flex h-10 w-10 items-center justify-center border border-cyan-400/30 text-cyan-300 lg:hidden"
          aria-label="Menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-cyan-400/10 bg-[#050714]/95 backdrop-blur-xl lg:hidden"
          >
            <nav className="flex flex-col gap-1 px-4 py-6">
              {links.map((link, i) => (
                <motion.a
                  key={link.id}
                  href={link.href}
                  data-testid={`mobile-nav-link-${link.id}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i }}
                  className="flex items-center justify-between border-b border-white/5 py-4 text-sm uppercase tracking-[0.25em] text-slate-200"
                >
                  {link.label}
                  <span className="font-pixel text-[10px] text-cyan-400">0{i + 1}</span>
                </motion.a>
              ))}
              <div className="mt-6 flex items-center justify-between">
                <div className="flex items-center gap-1 border border-cyan-400/20 p-1">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`mobile-lang-btn-${l.code}`}
                      onClick={() => setLang(l.code)}
                      className={`font-pixel px-3 py-1.5 text-[10px] tracking-widest ${
                        lang === l.code ? "bg-cyan-400 text-[#050714]" : "text-slate-400"
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-whatsapp-btn"
                  className="pixel-corners-sm flex items-center gap-2 bg-cyan-400 px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[#050714]"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
